import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Table, Row, Col, Form, InputGroup, Pagination, Modal } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useAdminListQuotesQuery,
  useAdminDeleteQuoteMutation
} from '../../slices/quotesApiSlice';
import Loader from '../../Components/Loader.jsx';
import Message from '../../Components/Message.jsx';
import BackButton from "../../Components/BackButton";

function ConvertButton({ quote }) {
  const navigate = useNavigate();
  const hasOrder = !!quote.orderId;
  const hasInvoice = !!(quote.invoice || hasOrder || quote.orderRef);

  // If there is already an order → show "View Order"
  if (hasInvoice) {
    if (hasOrder) {
      return (
        <Button
          className="btn-sm quote-invoice-btn btn-view-invoice"
          variant="info"
          onClick={() => navigate(`/order/${quote.orderId}`)}
        >
          View Order
        </Button>
      );
    }

    return (
      <Button
        className="btn-sm quote-invoice-btn btn-view-invoice"
        variant="secondary"
        disabled
      >
        Order created
      </Button>
    );
  }

  // Otherwise → allow making the invoice
  const canConvert = !['DECLINED', 'EXPIRED'].includes(quote.status);
  const disabled = !canConvert;

  return (
    <Button
      className="btn-sm quote-invoice-btn"
      variant="info"
      disabled={disabled}
      onClick={() => navigate(`/admin/quotes/${quote._id}/convert`)}
    >
      Create Order
    </Button>
  );
}

const shortRef = (v) =>
  (v ?? '')
    .toString()
    .replace(/[^a-f0-9]/gi, '')
    .slice(-6)
    .toUpperCase() || '—';

const QuoteListScreen = () => {
  const navigate = useNavigate();
  const confirmActionRef = useRef(null);
  const [confirmState, setConfirmState] = useState({
    show: false,
    title: '',
    body: '',
    confirmText: 'Confirm',
    variant: 'primary',
  });

  // --- URL state (so refresh/share keeps filters) -----------------------------
  const [sp, setSp] = useSearchParams();
  const [filters, setFilters] = useState({
    page: Number(sp.get('page') || 1),
    limit: Number(sp.get('limit') || 20),
    number: sp.get('number') || '',
    customer: sp.get('customer') || '',
    reference: sp.get('reference') || '',
    from: sp.get('from') || '',
    to: sp.get('to') || '',
  });

  // keep URL in sync when local state changes
  useEffect(() => {
    const next = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) next.set(k, String(v));
    });
    setSp(next, { replace: true });
  }, [filters, setSp]);

  // --- Data ------------------------------------------------------------------
  const queryArgs = useMemo(
    () => ({
      page: filters.page,
      limit: filters.limit,
      number: filters.number.trim() || undefined,
      customer: filters.customer.trim() || undefined,
      reference: filters.reference.trim() || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
    }),
    [filters]
  );

  const { data, isLoading, error, refetch, isFetching } =
   useAdminListQuotesQuery(queryArgs, { refetchOnMountOrArgChange: true });
  const [deleteQuote, { isLoading: deleting }] = useAdminDeleteQuoteMutation();

  const openConfirm = ({ title, body, confirmText, variant, onConfirm }) => {
    confirmActionRef.current = onConfirm;
    setConfirmState({
      show: true,
      title,
      body,
      confirmText,
      variant,
    });
  };

  const closeConfirm = () =>
    setConfirmState((state) => ({
      ...state,
      show: false,
    }));

  const handleConfirm = async () => {
    const action = confirmActionRef.current;
    closeConfirm();
    if (action) {
      await action();
    }
  };

  const createHandler = () => {
    openConfirm({
      title: 'Create a new quote?',
      body: 'This will open a new quote draft so you can add items, customer details, and pricing.',
      confirmText: 'Create quote',
      variant: 'primary',
      onConfirm: async () => {
        try {
          navigate('/admin/quotes/new/edit');
        } catch (err) {
          toast.error(err?.data?.message || err.error);
        }
      },
    });
  };

  const deleteHandler = (id) => {
    openConfirm({
      title: 'Delete this quote?',
      body: 'This action permanently removes the quote and cannot be undone.',
      confirmText: 'Delete quote',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deleteQuote(id).unwrap();
          toast.success('Quote deleted');
          refetch();
        } catch (err) {
          toast.error(err?.data?.message || err.error);
        }
      },
    });
  };

  const onChange = (key) => (e) =>
    setFilters((s) => ({ ...s, page: 1, [key]: e.target.value }));

  const onDate = (key) => (e) =>
    setFilters((s) => ({ ...s, page: 1, [key]: e.target.value }));

  const resetFilters = () =>
    setFilters({ page: 1, limit: 20, number: '', customer: '', reference: '', from: '', to: '' });

  const pages = data?.pages ?? 1;
  const page = data?.page ?? filters.page;

  // --- UI --------------------------------------------------------------------
  return (
    <>
    <BackButton />
      <Row className="align-items-center mb-3">
        <Col><h1>Quotes</h1></Col>
        <Col className="text-end">
          <Button onClick={createHandler}>Create Quote</Button>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="g-2 align-items-end mb-3">
        <Col md={2}>
          <Form.Label>Quote #</Form.Label>
          <Form.Control
            placeholder="KCC-Q-2025..."
            value={filters.number}
            onChange={onChange('number')}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Customer</Form.Label>
          <Form.Control
            placeholder="Name / Email"
            value={filters.customer}
            onChange={onChange('customer')}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Reference</Form.Label>
          <Form.Control
            placeholder="PO / Ref"
            value={filters.reference}
            onChange={onChange('reference')}
          />
        </Col>
        <Col md={2}>
          <Form.Label>From</Form.Label>
          <Form.Control type="date" value={filters.from} onChange={onDate('from')} />
        </Col>
        <Col md={2}>
          <Form.Label>To</Form.Label>
          <Form.Control type="date" value={filters.to} onChange={onDate('to')} />
        </Col>
        <Col md={2}>
          <Form.Label>Rows / page</Form.Label>
          <InputGroup>
            <Form.Select
              value={filters.limit}
              onChange={(e) => setFilters((s) => ({ ...s, page: 1, limit: Number(e.target.value) }))}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
            <Button variant="outline-secondary" onClick={resetFilters}>Reset</Button>
          </InputGroup>
        </Col>
      </Row>

      {( deleting || isFetching) && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || error.error}</Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm align-middle">
            <thead>
              <tr>
                <th>NUMBER</th>
                <th>STATUS</th>
                <th>CUSTOMER</th>
                <th>REFERENCE</th>
                <th>TOTAL</th>
                <th>DATE</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data?.quotes?.map((q) => {
                const hasInvoice = !!(q.invoice || q.orderId || q.orderRef);
                return (
                  <tr key={q._id}>
                  <td>{q.number}</td>
                  <td>{q.status}</td>
                  <td>{q.customerName}</td>
                 <td>
                    {shortRef(q.referenceShort || q.reference || q.orderRef || q.orderId)}
                  </td>
                  <td className="text-end">
                    ${Number(q.total ?? 0).toFixed(2)}
                  </td>
                  <td>{new Date(q.createdAt).toLocaleDateString()}</td>
                  <td className="text-end">
                    <div className="d-flex flex-wrap justify-content-end align-items-center gap-2">
                      <Button
                        className="btn-sm"
                        variant="success"
                        disabled={hasInvoice}
                        onClick={() => navigate(`/admin/quotes/${q._id}/edit`)}
                      >
                        Edit
                      </Button>
                      <Button className="btn-sm" variant="danger" onClick={() => deleteHandler(q._id)}>
                        Delete
                      </Button>
                      <ConvertButton quote={q} />
                    </div>
                  </td>
                </tr>
              );
            })}
            </tbody>
          </Table>

          {/* Pagination */}
          <Row className="align-items-center">
            <Col>
              <small className="text-muted">
                Page {page} of {pages} — {data?.total ?? data?.quotes?.length ?? 0} total
              </small>
            </Col>
            <Col className="d-flex justify-content-center">
              <Pagination className="mb-0">
                <Pagination.First
                  disabled={page <= 1}
                  onClick={() => setFilters((s) => ({ ...s, page: 1 }))}
                />
                <Pagination.Prev
                  disabled={page <= 1}
                  onClick={() => setFilters((s) => ({ ...s, page: s.page - 1 }))}
                />
                {Array.from({ length: pages || 1 }).slice(0, 7).map((_, i) => {
                  const p = i + 1;
                  return (
                    <Pagination.Item
                      key={p}
                      active={p === page}
                      onClick={() => setFilters((s) => ({ ...s, page: p }))}
                    >
                      {p}
                    </Pagination.Item>
                  );
                })}
                <Pagination.Next
                  disabled={page >= pages}
                  onClick={() => setFilters((s) => ({ ...s, page: s.page + 1 }))}
                />
                <Pagination.Last
                  disabled={page >= pages}
                  onClick={() => setFilters((s) => ({ ...s, page: pages }))}
                />
              </Pagination>
            </Col>
          </Row>
        </>
      )}

      <Modal
        show={confirmState.show}
        onHide={closeConfirm}
        centered
        contentClassName="confirm-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{confirmState.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-muted">{confirmState.body}</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeConfirm}>
            Cancel
          </Button>
          <Button variant={confirmState.variant} onClick={handleConfirm}>
            {confirmState.confirmText}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default QuoteListScreen;
